import { InvestmentRevaluationOption } from 'further-types/lp/investment';
import { RevaluationType } from 'further-types/lp/revaluation';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { investment as rules } from 'further-ui/validations';
import { round, sumBy } from 'lodash';
import { HoldingClassInvestmentTotal } from 'routes/LpPages/Company/Revaluations/CreateRevaluation/types';
import { AssetFullHistoryItem } from 'routes/LpPages/Company/Revaluations/FormWrapper';
import { InvestmentHistory } from 'routes/LpPages/Company/Revaluations/types';

export type RevaluationHoldingClass = {
  holdingClassId?: string;
  investmentAmountTcy: number;
  numberOfUnits: number;
  currency: CurrencyCode;
  unitPriceTcy: number;
};

type NewRevaluations = NonNullable<
  rules.CreateInvestmentFormValues['newRevaluations']
>;

const unitsForHoldingClass = (
  investmentHistory: InvestmentHistory,
  holdingClassId: string,
) =>
  sumBy(
    investmentHistory.filter(
      (investment) => investment?.holdingClassId === holdingClassId,
    ),
    (investment) => investment.currentNumberOfUnits,
  );

// "Revalue all existing shares": every holding class is revalued at the
// blended unit price of the new investment, and hold values are recomputed
// from each holding class's current units.
export const deriveAllSharesRevaluations = (
  baseNewRevaluations: NewRevaluations,
  holdingClasses: Array<RevaluationHoldingClass>,
  investmentHistory: InvestmentHistory,
) => {
  const totalInvestmentAmount = sumBy(
    holdingClasses,
    (holdingClass) => holdingClass.investmentAmountTcy,
  );
  const totalUnitsFromNewInvestment = sumBy(
    holdingClasses,
    (holdingClass) => holdingClass.numberOfUnits,
  );
  const averageUnitPrice =
    totalUnitsFromNewInvestment > 0
      ? round(totalInvestmentAmount / totalUnitsFromNewInvestment, 10)
      : 0;

  const totalUnitsFromAllHoldings = sumBy(
    investmentHistory,
    (investment) => investment.currentNumberOfUnits,
  );
  const currentHoldValue =
    totalUnitsFromAllHoldings > 0
      ? round(averageUnitPrice * totalUnitsFromAllHoldings, 10)
      : 0;

  const newRevaluations = baseNewRevaluations.map((revaluation) => {
    const numberOfUnits = unitsForHoldingClass(
      investmentHistory,
      revaluation.holdingClassId ?? '',
    );

    return {
      ...revaluation,
      unitPriceTcy: averageUnitPrice,
      currentHoldValue:
        numberOfUnits > 0
          ? round(averageUnitPrice * numberOfUnits, 10)
          : revaluation.currentHoldValue,
    };
  });

  return {
    currentUnitPrice: averageUnitPrice,
    currentHoldValue,
    newRevaluations,
  };
};

// "Revalue this share class only": each revalued holding class takes the unit
// price entered for the matching new investment holding class; others are left
// at their baseline.
export const deriveShareClassRevaluations = (
  baseNewRevaluations: NewRevaluations,
  holdingClasses: Array<RevaluationHoldingClass>,
  investmentHistory: InvestmentHistory,
) => {
  const newRevaluations = baseNewRevaluations.map((revaluation) => {
    const holdingClass = holdingClasses.find(
      (hc) => hc.holdingClassId === revaluation.holdingClassId,
    );

    if (!holdingClass) {
      return revaluation;
    }

    const numberOfUnits = unitsForHoldingClass(
      investmentHistory,
      revaluation.holdingClassId ?? '',
    );

    return {
      ...revaluation,
      unitPriceTcy: holdingClass.unitPriceTcy,
      currentHoldValue:
        numberOfUnits > 0
          ? round(holdingClass.unitPriceTcy * numberOfUnits, 10)
          : revaluation.currentHoldValue,
    };
  });

  return { newRevaluations };
};

export type RevaluationBaseline = {
  newRevaluations: NewRevaluations;
  initialUnitPrice: number;
  initialHoldValue: number;
};

// Builds one baseline (draft) revaluation per holding class from the asset's
// latest values, plus the opening overall unit price / hold value. Always
// reflects the asset's current state (never carries over previously derived
// values) so the automatic options derive from clean data. Pure: no form, no
// effect.
export const buildBaselineRevaluations = ({
  holdingClassInvestmentTotals,
  assetFullHistory,
  assetId,
  assetBaseCurrency,
}: {
  holdingClassInvestmentTotals: Array<HoldingClassInvestmentTotal>;
  assetFullHistory: Record<string, Array<AssetFullHistoryItem>>;
  assetId: string;
  assetBaseCurrency: CurrencyCode;
}): RevaluationBaseline => {
  const newRevaluations = holdingClassInvestmentTotals.map((total) => {
    const holdingClassId = total.holdingClassId;
    const holdingClassHistory = assetFullHistory[holdingClassId] ?? [];

    return {
      id: holdingClassId,
      revaluationId: undefined,
      holdingClassId,
      type: RevaluationType.Revaluation as const,
      assetId,
      holdingClassName: holdingClassHistory[0]?.holdingClassName ?? '',
      unitPriceTcy: total.latestUnitPrice ?? 0,
      currentHoldValue: total.currentHoldValue ?? 0,
      totalNumberOfUnits: total.totalNumberOfUnits ?? 0,
      currentNumberOfUnits: total.currentNumberOfUnits ?? 0,
      ledgerDate: new Date(),
      currency: total.currency ?? assetBaseCurrency,
      isRemoved: false,
      isNew: true,
    };
  });

  const initialHoldValue = sumBy(
    holdingClassInvestmentTotals,
    (total) => total.currentHoldValue,
  );
  const totalUnits = sumBy(
    holdingClassInvestmentTotals,
    (total) => total.currentNumberOfUnits,
  );
  const initialUnitPrice =
    totalUnits > 0 ? round(initialHoldValue / totalUnits, 10) : 0;

  return { newRevaluations, initialUnitPrice, initialHoldValue };
};

// Single source of the revaluation maths for a chosen option. Returns the
// values to write onto the form; the caller decides how to write them.
export const applyRevaluationOption = (
  option: InvestmentRevaluationOption | undefined,
  baseline: RevaluationBaseline,
  holdingClasses: Array<RevaluationHoldingClass>,
  investmentHistory: InvestmentHistory,
): {
  newRevaluations: NewRevaluations;
  currentUnitPrice: number;
  currentHoldValue: number;
} => {
  switch (option) {
    case InvestmentRevaluationOption.AllShares:
      return deriveAllSharesRevaluations(
        baseline.newRevaluations,
        holdingClasses,
        investmentHistory,
      );
    case InvestmentRevaluationOption.ShareClassOnly:
      return {
        ...deriveShareClassRevaluations(
          baseline.newRevaluations,
          holdingClasses,
          investmentHistory,
        ),
        currentUnitPrice: baseline.initialUnitPrice,
        currentHoldValue: baseline.initialHoldValue,
      };
    case InvestmentRevaluationOption.ManuallyInput:
      return {
        newRevaluations: baseline.newRevaluations,
        currentUnitPrice: baseline.initialUnitPrice,
        currentHoldValue: baseline.initialHoldValue,
      };
    case InvestmentRevaluationOption.DoNotRevalue:
    default:
      return {
        newRevaluations: [],
        currentUnitPrice: baseline.initialUnitPrice,
        currentHoldValue: baseline.initialHoldValue,
      };
  }
};
